'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import ReactFlow, { Background, Controls, Handle, Position, useEdgesState, useNodesState } from 'reactflow';
import 'reactflow/dist/style.css';
import { Device, DevicesApiResponse } from '@repo/shared/domain';
import { guardedClient as clientRequest } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';

// --- API ---

const getDevices = async (): Promise<DevicesApiResponse> => {
	const { body } = await clientRequest.get<undefined, DevicesApiResponse>(`/api/devices?noPagination=true`, {
		schema: undefined,
	});
	return body;
};

// --- Route ---

export const Route = createFileRoute('/_admin/config/devices/topology')({
	component: TopologyPage,
});

// --- Node Components ---

const UtilityNode = ({ data }: { data: { label: string } }) => (
	<>
		<Handle type="source" position={Position.Right} />
		<div
			style={{
				margin: 15,
				width: 10,
				height: 10,
				border: '1px solid rgba(16, 24, 32, 1)',
				backgroundColor: 'rgba(16, 24, 32, 0.3)',
				borderRadius: 20,
				lineHeight: 1.8,
			}}
		/>
		<div
			style={{
				position: 'absolute',
				top: 12,
				right: 30,
				textAlign: 'right',
				fontSize: 10,
				fontWeight: 'bold',
				width: 100,
			}}
		>
			{data?.label}
		</div>
	</>
);

const ReaderNode = ({ data }: { data: any }) => (
	<>
		<Handle type="target" position={Position.Left} />
		<Handle type="target" position={Position.Left} id="network" style={{ top: 30, background: '#3498db' }} />
		<div
			style={{
				width: 40,
				height: 40,
				padding: 5,
				border: '1px solid rgba(16, 24, 32, 1)',
				borderRadius: 5,
				lineHeight: 1.8,
				fontWeight: 'bold',
				textAlign: 'center',
				backgroundColor: 'rgba(16, 24, 32, 0.8)',
				color: 'white',
			}}
		></div>
		<div
			style={{
				position: 'absolute',
				left: 0,
				fontSize: 6,
				width: 100,
			}}
		>
			<p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
		</div>
		<Handle type="source" position={Position.Right} id="successHook" style={{ top: 10, background: '#2ecc71' }} />
		<Handle type="source" position={Position.Right} id="errorHook" style={{ top: 30, background: '#e74c3c' }} />
	</>
);

const InputNode = () => (
	<>
		<Handle type="source" position={Position.Top} />
		<div
			style={{
				margin: 15,
				width: 10,
				height: 10,
				borderRadius: 20,
				lineHeight: 1.8,
			}}
		/>
	</>
);

const HookNode = ({ data }: { data: any }) => (
	<>
		<Handle type="target" position={Position.Left} />
		<div
			style={{
				margin: 15,
				width: 10,
				height: 10,
				border: '1px solid rgba(16, 24, 32, 1)',
				backgroundColor: 'rgba(16, 24, 32, 0.3)',
				borderRadius: 20,
				lineHeight: 1.8,
			}}
		/>
		<div
			style={{
				position: 'absolute',
				top: 12,
				left: 30,
				fontSize: 10,
				fontWeight: 'bold',
				width: 200,
			}}
		>
			{data.label}
		</div>
	</>
);

const ControllerNode = ({ data }: { data: any }) => (
	<>
		<div
			style={{
				width: 70,
				height: 40,
				padding: 5,
				border: '1px solid rgba(16, 24, 32, 1)',
				borderRadius: 5,
				lineHeight: 1.8,
				fontWeight: 'bold',
				textAlign: 'center',
				backgroundColor: data.hasError ? '#e74c3c' : 'rgba(16, 24, 32, 0.8)',
				color: 'white',
			}}
		/>

		<div
			style={{
				position: 'absolute',
				textAlign: 'right',
				right: 80,
				top: 20,
				fontSize: 6,
				width: 100,
			}}
		>
			<p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
		</div>

		<Handle type="source" position={Position.Right} id="nodes" />
		<Handle type="target" position={Position.Top} id="network" style={{ left: 10, background: '#3498db' }} />
		<Handle type="target" position={Position.Top} id="power" style={{ left: 15, background: '#f39c12' }} />
		<Handle type="target" position={Position.Bottom} id="I1" style={{ left: 30, bottom: -2.5 }} />
		<Handle type="target" position={Position.Bottom} id="I2" style={{ left: 40, bottom: -2.5 }} />
		<Handle type="target" position={Position.Bottom} id="I3" style={{ left: 50, bottom: -2.5 }} />
		<Handle type="target" position={Position.Bottom} id="I4" style={{ left: 60, bottom: -2.5 }} />
		<Handle type="source" position={Position.Top} id="R1" style={{ left: 30, top: -2.5 }} />
		<Handle type="source" position={Position.Top} id="R2" style={{ left: 40, top: -2.5 }} />
		<Handle type="source" position={Position.Top} id="R3" style={{ left: 50, top: -2.5 }} />
		<Handle type="source" position={Position.Top} id="R4" style={{ left: 60, top: -2.5 }} />
	</>
);

// --- Node Types ---

const nodeTypes = {
	UtilityNode,
	ReaderNode,
	ControllerNode,
	HookNode,
	InputNode,
};

// --- Topology Graph ---

interface TopologyGraphProps {
	devices: Device[];
}

function TopologyGraph({ devices }: TopologyGraphProps) {
	const [nodes, setNodes] = useNodesState([]);
	const [edges, setEdges] = useEdgesState([]);

	const loadData = useCallback(async () => {
		const emptyPos = { x: 0, y: 0 };

		const filteredDevices = devices.filter((d) => d.type !== 'kiosk');
		const nodes: any[] = [
			{
				id: 'power',
				position: { x: -100, y: -100 },
				data: { label: 'Power' },
				type: 'UtilityNode',
			},
			{
				id: 'network',
				position: { x: -100, y: -150 },
				data: { label: 'Network' },
				type: 'UtilityNode',
			},
		];
		const edges: Array<{
			id: string;
			source: string;
			target: string;
			type: string;
			targetHandle?: string;
			sourceHandle?: string;
			style?: any;
		}> = [];

		filteredDevices.forEach((device) => {
			const pos = device.graph?.position || { x: emptyPos.x, y: emptyPos.y };
			const deviceNode = {
				id: device.id,
				position: pos,
				data: device,
				type: 'ControllerNode',
			};
			edges.push({
				id: `${device.id}-power-edge`,
				source: 'power',
				target: device.id,
				targetHandle: 'power',
				type: 'smoothstep',
				style: { stroke: '#f39c12', opacity: 0.2 },
			});
			edges.push({
				id: `${device.id}-network-edge`,
				source: 'network',
				target: device.id,
				targetHandle: 'network',
				type: 'smoothstep',
				style: { stroke: '#3498db', opacity: 0.2 },
			});

			// Readers
			const readers = device.config?.controller?.nodes || [];
			readers.forEach((reader: any, index: number) => {
				nodes.push({
					id: reader.id,
					position: reader.graph || { x: pos.x + 140, y: pos.y + 80 * index },
					data: reader,
					type: 'ReaderNode',
				});
				edges.push({
					id: `${reader.id}-edge`,
					source: device.id,
					target: reader.id,
					type: 'smoothstep',
					style: { strokeDasharray: 3 },
				});
				edges.push({
					id: `${reader.id}-hook`,
					source: reader.id,
					target: `${device.id}-${reader.authSuccessHook}-hook`,
					type: 'smoothstep',
					style: { strokeDasharray: 3 },
				});
				edges.push({
					id: `${reader.id}-network`,
					target: reader.id,
					targetHandle: 'network',
					source: 'network',
					type: 'smoothstep',
					style: { stroke: '#3498db', opacity: 0.2 },
				});
			});

			// Inputs
			const inputs = device.config?.inputs || [];
			Object.entries(inputs).forEach(([id, i]: [string, any], index: number) => {
				nodes.push({
					id: `${device.id}-${id}-input`,
					position: i.graph || { x: pos.x + 10 + 10 * index, y: pos.y + 60 },
					data: i,
					type: 'InputNode',
				});
				edges.push({
					id: `${device.id}-${id}-edge`,
					target: device.id,
					source: `${device.id}-${id}-input`,
					targetHandle: id,
					type: 'smoothstep',
					style: { stroke: '#16a085', opacity: 0.2 },
				});
			});

			// Hooks
			const hooks = device.config?.hooks || [];
			Object.entries(hooks).forEach(([id, hook]: [string, any], index: number) => {
				nodes.push({
					id: `${device.id}-${id}-hook`,
					position: hook.graph || {
						x: pos.x + 240 + 20 * index,
						y: pos.y - 120 + index * 20,
					},
					data: hook,
					type: 'HookNode',
				});
				(Array.isArray(hook) ? hook : hook.actions)?.forEach((a: any) => {
					if (a.type !== 'internal' || !a.port) return;
					edges.push({
						id: `${device.id}-${id}-edge`,
						source: device.id,
						target: `${device.id}-${id}-hook`,
						sourceHandle: a.port,
						type: 'smoothstep',
						style: { stroke: '#8e44ad' },
					});
				});
			});

			if (!device.graph?.position) {
				emptyPos.y += (readers.length || 1) * 80 + 100;
			}
			if (emptyPos.y >= 2000) {
				emptyPos.y = 0;
				emptyPos.x += 800;
			}
			nodes.push(deviceNode);
		});

		setNodes(nodes);
		setEdges(edges);
	}, [devices, setEdges, setNodes]);

	useEffect(() => {
		loadData();
	}, [loadData]);

	return (
		<ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView minZoom={0.2} snapGrid={[20, 20]} snapToGrid>
			<Controls />
			<Background />
		</ReactFlow>
	);
}

// --- Page Component ---

function TopologyPage() {
	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Devices');
	useBreadcrumbLevel(2, 'Topology');

	const { data: devicesData, isLoading } = useQuery({
		queryKey: ['devices', 'noPagination'],
		queryFn: getDevices,
	});

	if (isLoading || !devicesData) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	return (
		<div style={{ width: '100%', height: 'calc(100vh - 120px)' }}>
			<TopologyGraph devices={devicesData.data} />
		</div>
	);
}
